/**
 * Event attendance / no-show reporting service.
 *
 * Thin wrappers over the backend-owned attendance resolver (migration 2026-08-13-001)
 * and the snapshot-consistent CSV export (2026-08-13-003). The backend enforces
 * event-scoped authorization (can_operate_event_reward) inside every SECURITY DEFINER
 * RPC; these calls run as the signed-in user.
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

// The cohort union lives in utils/eventAttendance.ts alongside the canonical
// arithmetic, so the resolver's contract has exactly ONE definition in the frontend.
export type { AttendanceCohort } from "../utils/eventAttendance";
import type { AttendanceCohort } from "../utils/eventAttendance";

export interface AttendanceSummary {
  registered_rows: number;
  registered_headcount: number;
  registered_member_headcount: number;
  registered_nonmember_headcount: number;
  checked_in_rows: number;
  checked_in_registered_headcount: number;
  // Verified bodies present (verified where staff recorded counts, else registered).
  attended_headcount: number;
  attended_member_headcount: number;
  attended_nonmember_headcount: number;
  walk_in_rows: number;
  walk_in_headcount: number;
  awaiting_rows: number;
  awaiting_headcount: number;
  no_show_rows: number;
  no_show_headcount: number;
  cancelled_rows: number;
  incomplete_rows: number;
  attributed_rows: number;
  other_rows: number;
  member_shortfall: number;
  nonmember_shortfall: number;
  attendance_rate: number | null;
}

export interface AttendanceRosterRow {
  attendee_id: UUID;
  contact_id: UUID | null;
  prospect_id: UUID | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  attendee_type: string | null;
  current_member: boolean;
  registration_source: string | null;
  registration_date: string | null;
  attendance_status: string | null;
  checked_in_at: string | null;
  is_walk_in: boolean;
  cohort: AttendanceCohort;
  registered_member_count: number;
  registered_nonmember_count: number;
  registered_headcount: number;
  attended_member_count: number | null;
  attended_nonmember_count: number | null;
  attended_headcount: number | null;
  member_shortfall: number;
  nonmember_shortfall: number;
  invited_by_name: string | null;
  is_contactable: boolean;
  sort_ts: string;
}

export interface MaterializeNoshowResult {
  list_id: UUID;
  source_attendee_count: number;
  materialized_contact_count: number;
  excluded_unlinked_count: number;
  excluded_no_email_count: number;
}

export async function getAttendanceSummary(
  eventId: UUID,
): Promise<AttendanceSummary> {
  const { data, error } = await supabase.rpc("get_event_attendance_summary", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row as AttendanceSummary;
}

export interface RosterPage {
  rows: AttendanceRosterRow[];
  nextCursor: { sort: string; id: UUID } | null;
}

export async function getAttendanceRosterPage(
  eventId: UUID,
  cohort: AttendanceCohort,
  cursor: { sort: string; id: UUID } | null,
  limit = 100,
): Promise<RosterPage> {
  const { data, error } = await supabase.rpc("get_event_attendance_roster", {
    p_event_id: eventId,
    p_cohort: cohort,
    p_after_sort: cursor?.sort ?? null,
    p_after_id: cursor?.id ?? null,
    p_limit: limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as AttendanceRosterRow[];
  const last = rows[rows.length - 1];
  const nextCursor =
    rows.length === limit && last
      ? { sort: last.sort_ts, id: last.attendee_id }
      : null;
  return { rows, nextCursor };
}

export async function materializeNoshowList(
  eventId: UUID,
): Promise<MaterializeNoshowResult> {
  const { data, error } = await supabase.rpc("materialize_event_noshow_list", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row as MaterializeNoshowResult;
}

/**
 * Download the complete, snapshot-consistent attendance CSV via the edge function.
 * Triggers a browser download; throws a friendly message on 403 / 413 / error.
 */
export async function downloadAttendanceCsv(
  eventId: UUID,
  cohort: AttendanceCohort,
  eventTitle?: string,
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error("No active session");
  }

  const base = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(`${base}/functions/v1/export-event-attendance`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      apikey: anon,
    },
    body: JSON.stringify({ event_id: eventId, cohort }),
  });

  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.error) {
        msg = j.error;
      }
    } catch {
      // non-JSON body; keep the status message
    }
    if (res.status === 413) {
      msg = "This event is too large for a direct download. " + msg;
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const headerName = res.headers.get("X-Filename");
  const safeTitle =
    (eventTitle || "event")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "event";
  const filename = headerName || `${safeTitle}-attendance.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
